import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useCourse } from '@/contexts/CourseContext';
import { Button } from '@/components/ui/button';
import { Loader2, Check, X } from 'lucide-react';
import LearningObjectivesStepShell from '@/components/step-shells/LearningObjectivesStepShell';

export default function Step2LearningObjectives() {
  const { course, updateCourse, generateGoals, loading, sendChatMessage, sendAssistantMessage, dispatch, resetChatHistory, welcomesSent, importMode } = useCourse();
  const [stepInitialized, setStepInitialized] = useState(false);
  const [pendingObjective, setPendingObjective] = useState(null);

  // Initialize Step 2 when component mounts
  useEffect(() => {
    const initializeStep2 = async () => {
      if (course && !stepInitialized && !welcomesSent?.[2]) {
        setStepInitialized(true);

        if (!importMode) {
          // Mark welcome as sent IMMEDIATELY to prevent duplicate calls
          dispatch({ type: 'SET_WELCOME_SENT', payload: 2 });

          // Regular build mode: generate learning objectives if none exist
          if (!course.learningObjectives || course.learningObjectives.length === 0) {
            console.log('Step2LearningObjectives: Initializing - will auto-generate objectives...');
            await generateGoals();
          }

          // Use course.language from context - no need for extra API call
          const courseLanguage = course?.language || 'English';
          const isGreek = courseLanguage.toLowerCase() === 'greek' || courseLanguage.toLowerCase() === 'ελληνικά';

          const greetingMessage = isGreek
            ? "**Καλώς ήρθατε στο Βήμα 2!** Η βάση του μαθήματος έχει οριστεί.\n\nΤώρα θα ορίσουμε τι θα επιτύχουν οι μαθητές σας - τους μαθησιακούς στόχους που θα καθοδηγήσουν όλα όσα θα χτίσουμε.\n\nΈχω δημιουργήσει κάποιους αρχικούς στόχους με βάση το μάθημά σας. Ρίξτε τους μια ματιά στο πλαϊνό πάνελ - μπορούμε να τους βελτιώσουμε μαζί, να προσθέσουμε νέους, ή να προσαρμόσουμε τα επίπεδα Bloom.\n\n**Σε τι θα θέλατε να εστιάσουμε πρώτα;**\n\nΌταν θέλετε να προχωρήσετε στο επόμενο βήμα, απλά ενημερωσέ με."
            : "**Welcome to Step 2!** The course foundation is set.\n\nNow we'll define what your students will achieve - the learning objectives that will guide everything we build.\n\nI've drafted some initial objectives based on your course. Take a look at them in the panel - we can refine them together, add new ones, or adjust the Bloom's levels.\n\n**What would you like to focus on first?**\n\nWhenever you're ready to move on to the next step, just ask me.";

          dispatch({ type: 'ADD_CHAT_MESSAGE', payload: {
            role: 'assistant',
            text: greetingMessage
          } });
        }
        // In import mode, learning objectives are already loaded
      }
    };
    initializeStep2();
  }, [course, stepInitialized, welcomesSent, importMode, generateGoals, dispatch]);

  // REMOVED: This useEffect was causing duplicate API calls after every tool execution.
  // The welcome message is now handled only in the first useEffect (initializeStep2)
  // which properly guards against re-running with stepInitialized state.

  const handleGenerate = async () => {
    await generateGoals();
  };

  const handleAcceptPending = async () => {
    if (!course || !pendingObjective) return;
    // Already added by backend; nothing to do other than clear pending tab
    setPendingObjective(null);
  };

  const handleRejectPending = async () => {
    if (!course || !pendingObjective) return;
    // Remove the pending objective that backend already added
    const updated = (course.learningObjectives || []).filter(obj => obj.id !== pendingObjective.id);
    await updateCourse({ learningObjectives: updated });
    setPendingObjective(null);
    // Ask if they want another of the same type
    sendChatMessage("I didn't like that objective. Please generate another objective of the same Bloom level.");
  };

  const hasObjectives = course && course.learningObjectives && course.learningObjectives.length > 0;

  // Surface newly generated single objective provided by backend on course_data.lastGeneratedGoal
  useEffect(() => {
    if (course && course.lastGeneratedGoal) {
      if (!pendingObjective || pendingObjective.id !== course.lastGeneratedGoal.id) {
        setPendingObjective(course.lastGeneratedGoal);
      }
    }
  }, [course, pendingObjective]);

  const handleEdit = async (path, value) => {
    if (!course) return;
    const pathParts = path.split('.');
    const updatedCourse = JSON.parse(JSON.stringify(course));

    let current = updatedCourse;
    for (let i = 0; i < pathParts.length - 1; i++) {
      const part = pathParts[i];
      const nextPart = pathParts[i + 1];
      const index = parseInt(nextPart);

      if (!isNaN(index)) {
        current = current[part][index];
        i++;
      } else {
        current = current[part];
      }
    }

    const lastKey = pathParts[pathParts.length - 1];
    current[lastKey] = value;

    await updateCourse(updatedCourse);
  };

  return (
    <LearningObjectivesStepShell
      objectives={course?.learningObjectives || []}
      onEdit={handleEdit}
      importMode={importMode}
    >
      {/* Generate button (regular mode, no objectives) */}
      {!importMode && !hasObjectives && (
        <div className="bg-white p-8 rounded-2xl shadow-xl border-2 border-teal-100 text-center">
          <Button
            onClick={handleGenerate}
            disabled={loading}
            data-testid="generate-objectives-button"
            className="py-6 px-8 text-lg font-bold bg-gradient-to-r from-teal-500 to-orange-500 hover:from-teal-600 hover:to-orange-600 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 animate-spin" size={24} />
                Generating Learning Objectives...
              </>
            ) : (
              <>Generate Learning Objectives with AI</>
            )}
          </Button>
        </div>
      )}

      {/* Pending objective approval */}
      {hasObjectives && pendingObjective && (
        <div className="rounded-2xl shadow-xl overflow-hidden">
          <div className="bg-white p-4 border-x-2 border-t-2 border-orange-200">
            <div className="text-xs text-gray-600 mb-1">New {pendingObjective.bloom_level} objective</div>
            <div className="text-gray-800 font-medium">{pendingObjective.text}</div>
          </div>
          <div className="grid grid-cols-2">
            <button
              onClick={handleAcceptPending}
              className="py-4 text-white font-semibold bg-green-500 hover:bg-green-600 transition-colors"
              aria-label="Accept objective"
            >
              <span className="inline-flex items-center justify-center">
                <Check size={18} className="mr-2" /> Keep
              </span>
            </button>
            <button
              onClick={async () => { await handleRejectPending(); await sendAssistantMessage('What didn\'t you like about that objective? Tell me and I\'ll adjust it.'); }}
              className="py-4 text-white font-semibold bg-red-500 hover:bg-red-600 transition-colors"
              aria-label="Reject objective"
            >
              <span className="inline-flex items-center justify-center">
                <X size={18} className="mr-2" /> Reject
              </span>
            </button>
          </div>
        </div>
      )}
    </LearningObjectivesStepShell>
  );
}
